import {KPI_DISPLAY_ORDER, KPI_METADATA} from "../lib";
import {dashboardService} from "../services";
import type {DashboardKpi, DashboardKpiKey} from "../types";
import type {KpiCardDensity} from "./kpi-card";
import {KpiCardDars} from "./kpi-card-dars";
import {KpiCardPermits} from "./kpi-card-permits";
import {KpiCardSafelistings} from "./kpi-card-safelistings";
import {KpiCardSimple} from "./kpi-card-simple";
import {KpiCardStoredVehicles} from "./kpi-card-stored-vehicles";

type KpisGridProps = {
  kpis: DashboardKpi[];
  selectedKey?: DashboardKpiKey;
  onSelectKey?: (key: DashboardKpiKey) => void;
  density?: KpiCardDensity;
};

export function KpisGrid({
  kpis,
  selectedKey,
  onSelectKey,
  density = "default",
}: KpisGridProps) {
  const indexed = dashboardService.indexKpis(kpis);

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {KPI_DISPLAY_ORDER.map((key) => (
          <div key={key} className="min-h-0 h-full min-w-0">
            <KpiSlot
              kpiKey={key}
              kpi={indexed[key]}
              density={density}
              selected={selectedKey === key}
              onSelect={onSelectKey ? () => onSelectKey(key) : undefined}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiSlot({
  kpiKey,
  kpi,
  density,
  selected,
  onSelect,
}: {
  kpiKey: DashboardKpiKey;
  kpi: DashboardKpi | undefined;
  density: KpiCardDensity;
  selected: boolean;
  onSelect?: () => void;
}) {
  const meta = KPI_METADATA[kpiKey];

  if (!kpi) return null;

  switch (kpi.key) {
    case "dars":
      return (
        <KpiCardDars
          kpi={kpi}
          icon={meta.icon}
          density={density}
          selected={selected}
          onSelect={onSelect}
        />
      );
    case "safelistings":
      return (
        <KpiCardSafelistings
          kpi={kpi}
          icon={meta.icon}
          density={density}
          selected={selected}
          onSelect={onSelect}
        />
      );
    case "permanent_permits":
      return (
        <KpiCardPermits
          kpi={kpi}
          icon={meta.icon}
          density={density}
          selected={selected}
          onSelect={onSelect}
        />
      );
    case "stored_vehicles":
      return (
        <KpiCardStoredVehicles
          kpi={kpi}
          icon={meta.icon}
          density={density}
          selected={selected}
          onSelect={onSelect}
        />
      );
    case "citations":
    case "incident_reports":
    case "amenity_access_logs":
    case "dispatch_logs":
      return (
        <KpiCardSimple
          label={kpi.label ?? meta.label}
          icon={meta.icon}
          current={kpi.current}
          previous={kpi.previous}
          deltaPct={kpi.delta_pct}
          density={density}
          selected={selected}
          onSelect={onSelect}
        />
      );
    default:
      return null;
  }
}
